// @ts-strict-ignore
import React, {useMemo} from 'react';
import clsx from 'clsx';

import {SelectReact} from 'common/components/select';
import {SelectTheme} from 'common/components/select/constants';
import {ButtonSize, ButtonTheme} from 'common/components/button/constants';
import {InputReact} from 'common/components/input';
import {InputSize, ValidationStatus} from 'common/components/input/types';
import {WINDOW_LENGTH_UNITS, WindowLengthUnit, WindowType} from 'common/types/reports/query';
import {capitalize, pluralize} from 'common/util/string';
import {ButtonReact} from 'common/components/button';
import {withStyles} from 'common/util/react';
import {useStagedState} from 'common/hooks/useStagedState';
import {parseNumber} from 'common/util';

import css from './index.module.styl?inline';
import styles from './index.module.styl';

const DEFAULT_LENGTH = 7;
const DEFAULT_LENGTH_UNIT = WindowLengthUnit.DAY;

type LookbackWindow = {
  windowLength?: number;
  windowLengthUnit?: WindowLengthUnit;
  windowType?: WindowType;
};

export const BehavioralLookbackWindowSubItem = withStyles(
  (props: {
    className?: string;
    lookbackWindow: LookbackWindow;
    open: boolean;
    onChange: (window: LookbackWindow) => void;
    onOpen: () => void;
    onClose: () => void;
  }) => {
    const [stagedLookbackWindow, setStagedLookbackWindow] = useStagedState(props.lookbackWindow, props.onChange);

    const invalidText = useMemo(() => {
      const {windowLength} = stagedLookbackWindow;

      if (windowLength <= 0) {
        return `Time must be an integer value.`;
      }

      if (windowLength > 90) {
        return `Time exceeds 90 day limit.`;
      }

      return ``;
    }, [stagedLookbackWindow]);

    return (
      <SelectReact
        className={clsx(styles.subitemSelect, props.className)}
        label={`${props.lookbackWindow.windowLength ?? DEFAULT_LENGTH} ${
          props.lookbackWindow.windowLengthUnit ?? DEFAULT_LENGTH_UNIT
        }s`}
        open={props.open}
        theme={SelectTheme.QbSubitem}
        size={ButtonSize.Small}
        preventClose
        onMenuChange={(ev) => {
          if (ev.detail.isOpen) {
            props.onOpen();
          } else {
            props.onClose();
          }
        }}
      >
        {props.open && (
          <div className={styles.windowSelectionContainer} slot='body'>
            <div className={styles.windowSelectionInputs}>
              <InputReact
                className={styles.windowLengthInput}
                helpText='The amount of time to calculate the behavior after the birth event'
                type='number'
                size={InputSize.Medium}
                validationStatus={invalidText ? ValidationStatus.Invalid : ValidationStatus.Default}
                value={stagedLookbackWindow.windowLength !== null ? stagedLookbackWindow.windowLength.toString() : ``}
                onInput={(ev) =>
                  setStagedLookbackWindow({
                    ...stagedLookbackWindow,
                    windowLength: parseNumber(ev.target.value),
                  })
                }
              />
              <SelectReact
                className={styles.windowLengthUnitInput}
                elref='windowLengthUnit'
                disableSearch
                sections={[
                  {
                    items: WINDOW_LENGTH_UNITS.map((unit) => ({
                      value: unit,
                      label: capitalize(pluralize(unit, true)),
                      isSelected: unit === stagedLookbackWindow.windowLengthUnit,
                    })),
                  },
                ]}
                selectedOption={capitalize(pluralize(stagedLookbackWindow.windowLengthUnit, true))}
                theme={SelectTheme.Tertiary}
                neverShowCaret
                tooltip={{
                  text: `Time Unit to be applied to the Window Length value`,
                }}
                onChange={(ev) =>
                  setStagedLookbackWindow({
                    ...stagedLookbackWindow,
                    windowLengthUnit: ev.detail.item.value,
                  })
                }
              />
            </div>
            {!!invalidText && <div className={styles.windowSelectionErrorMessage}>{invalidText}</div>}
            <div className={styles.windowSelectionFooter}>
              <ButtonReact
                className={styles.resetButton}
                elref='resetButton'
                theme={ButtonTheme.Tertiary}
                disabled={
                  stagedLookbackWindow.windowLength === DEFAULT_LENGTH &&
                  stagedLookbackWindow.windowLengthUnit === DEFAULT_LENGTH_UNIT
                }
                onClick={() =>
                  setStagedLookbackWindow({windowLength: DEFAULT_LENGTH, windowLengthUnit: DEFAULT_LENGTH_UNIT})
                }
              >
                Reset
              </ButtonReact>
              <div className={styles.coreBtnGroup}>
                <ButtonReact theme={ButtonTheme.Tertiary} elref='cancelButton' onClick={props.onClose}>
                  Cancel
                </ButtonReact>
                <ButtonReact
                  className={styles.applyButton}
                  theme={ButtonTheme.Primary}
                  disabled={!!invalidText}
                  elref='applyButton'
                  onClick={() =>
                    props.onChange?.({
                      ...stagedLookbackWindow,
                      windowType: WindowType.AFTER,
                    })
                  }
                >
                  Apply
                </ButtonReact>
              </div>
            </div>
          </div>
        )}
      </SelectReact>
    );
  },
  css,
);
